import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, pathFor, paxRoute, StreamResponse } from "../../common";
import {
  GetPresetResponse,
  ListPresetsQuery,
  ListPresetsResponse,
  PresetSlugParams,
  PresetVersionFileParams,
  PresetVersionParams,
} from "./schemas";

export const service = {
  group: "presets",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Presets API",
  description: "Published versioned scenarios and their immutable artifacts.",
} as const satisfies ContractService;

export const presetVersionFileRoute =
  "/api/presets/v1alpha1/versions/{versionId}/files/{file}" as const;

export function presetVersionFilePath(versionId: string, file: string): string {
  return pathFor(presetVersionFileRoute, { versionId, file });
}

export const list = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1",
      operationId: "presets.list",
      summary: "List game presets.",
      description:
        "Lists published user-owned presets from Postgres, optionally filtered by a case-insensitive title or slug search and by owner. Each result names its live version, the latest it published.",
      inputStructure: "detailed",
      successDescription: "One page of matching presets.",
    }),
  )
  .errors(errors.badRequest)
  .input(z.object({ query: ListPresetsQuery }))
  .output(ListPresetsResponse);

export const get = oc
  .meta(
    paxRoute({
      auth: { kind: "public", note: "A Better Auth session, when present, adds viewer state." },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/{slug}",
      operationId: "presets.get",
      summary: "Get a game preset.",
      description:
        "Resolves a preset slug to storefront metadata and one immutable seed-batch descriptor.",
      inputStructure: "detailed",
      successDescription: "The preset and its playable entities.",
    }),
  )
  .errors({
    ...errors.badRequest,
    NOT_FOUND: { message: "No preset has that slug." },
  })
  .input(z.object({ params: PresetSlugParams }))
  .output(GetPresetResponse);

export const getVersion = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/versions/{versionId}",
      operationId: "presets.versions.get",
      summary: "Get an immutable preset version.",
      description:
        "Returns the exact published preset version named, even after its preset publishes a newer one.",
      inputStructure: "detailed",
      successDescription: "The immutable preset version.",
    }),
  )
  .errors({
    ...errors.badRequest,
    NOT_FOUND: { message: "No published preset version has that id." },
  })
  .input(z.object({ params: PresetVersionParams }))
  .output(GetPresetResponse);

export const getFile = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: presetVersionFileRoute,
      operationId: "presets.versions.files.get",
      summary: "Download an immutable preset file.",
      description:
        "Streams one file declared by a published preset version directly from catalog R2.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      responseBodyHint: "octet-stream",
      successDescription: "The file, with its stored media type.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...errors.notFound,
  })
  .input(z.object({ params: PresetVersionFileParams }))
  .output(StreamResponse);
